import type { AccountProfile, SensitiveProfileField } from '@workflow/contracts'
import { Button, cn, EmptyNote } from '@workflow/ui'
import { PROFILE_GROUPS, paymentFieldVisible, profileFieldValue } from '@/features/account/profileFields'

/**
 * The shared profile composition — typeset, not boxed. The house language is
 * editorial: a group opens with its name in small caps over a full rule, the
 * way a dossier sets a section, and each fact is a single line that reads
 * from the muted label on the left to the answer, weighted, on the right.
 * No outlines, no chips; the rhythm and the rule do the separating.
 */

/** The headline facts of an account, as a quiet centered row of columns. */
export function FactsStrip({ facts }: { facts: { label: string; value: string }[] }) {
  return (
    <dl className="flex flex-wrap justify-center gap-x-12 gap-y-4">
      {facts.map((fact) => (
        <div key={fact.label} className="flex min-w-36 flex-col items-center gap-1">
          <dd className="order-1 font-display text-lg leading-tight text-foreground">{fact.value}</dd>
          <dt className="order-2 text-micro text-muted-foreground uppercase">{fact.label}</dt>
        </div>
      ))}
    </dl>
  )
}

/**
 * The profile as filed: each group a section under its rule, each fact one
 * confident line. The Discord UID is an identifier, and identifiers are what
 * mono is for.
 *
 * When `mask` is set — the admin view of someone else's record — the
 * sensitive fields render as the server sent them: behind the mask, with a
 * quiet Show beside each until it is revealed. A record seen whole is a
 * record a screenshot can spend; the button is the asking.
 */
export function ProfileGroups({
  profile,
  mask,
}: {
  profile: AccountProfile | null
  mask?: {
    revealed: ReadonlySet<SensitiveProfileField>
    onReveal: (field: SensitiveProfileField) => void
  }
}) {
  if (!profile) {
    return <EmptyNote title="No profile on file yet." detail="It appears here once they submit their profile." />
  }
  return (
    <div className="flex flex-col">
      {PROFILE_GROUPS.map((group) => (
        <section key={group.title} className="border-t border-hairline py-5 first:border-t-0 first:pt-0 last:pb-0">
          <h3 className="text-micro text-muted-foreground uppercase">{group.title}</h3>
          <dl className="mt-3 flex flex-col gap-2.5">
            {group.fields.filter((field) => group.title !== 'Payment Information' || paymentFieldVisible(field.key, profile.paymentMethod ?? '', profile.paymentAccountNumber ?? '')).map((field) => {
              const sensitive: SensitiveProfileField | null =
                field.key === 'identityNumber' || field.key === 'paymentAccountNumber' ? field.key : null
              const needsReveal = Boolean(mask && sensitive !== null && !mask.revealed.has(sensitive))
              const value = profileFieldValue(profile, field)
              return (
                <div key={field.key} className="flex items-baseline justify-between gap-6">
                  <dt className="shrink-0 text-caption text-muted-foreground">{field.key === 'paymentDetails' && profile.paymentMethod === 'custom' ? 'Custom payment details' : field.label}</dt>
                  <dd
                    className={cn(
                      'wf-selectable flex min-w-0 items-baseline gap-2 text-right text-ui font-medium text-foreground',
                      field.key === 'discordUserId' && 'font-mono text-[13px] font-normal',
                    )}
                  >
                    <span className="min-w-0 truncate">{value}</span>
                    {needsReveal && sensitive !== null && value !== '—' ? (
                      <Button size="xs" variant="ghost" onClick={() => mask!.onReveal(sensitive)}>
                        Show
                      </Button>
                    ) : null}
                  </dd>
                </div>
              )
            })}
          </dl>
        </section>
      ))}
    </div>
  )
}
